import { useState, useEffect } from 'react';
import { BRAND_CONFIG, BrandConfig } from '../config/brand';
import { fetchBrandConfig, isBrandApiConfigured } from '../services/brandService';

interface UseBrandConfigOptions {
  /** Optional tenant/brand ID to fetch specific configuration */
  brandId?: string;
  /** Optional API endpoint URL (overrides VITE_BRAND_API_URL) */
  apiEndpoint?: string;
  /** Auto-fetch on mount. Default: true */
  autoFetch?: boolean;
}

interface UseBrandConfigResult {
  /** The current brand configuration (fallback to embedded defaults) */
  config: BrandConfig;
  /** Loading state while fetching from API */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the config was loaded from API (vs embedded defaults) */
  isFromApi: boolean;
  /** Manually trigger a fetch/refresh of the config */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage BRAND_CONFIG fetching from backend API with fallback to embedded defaults
 * 
 * Usage:
 * ```tsx
 * const { config, isLoading, error, refetch } = useBrandConfig({ brandId: 'tenant-123' });
 * 
 * if (isLoading) return <LoadingScreen />;
 * if (error) console.warn('Using embedded brand config:', error);
 * 
 * // Use config...
 * ```
 * 
 * The hook will:
 * 1. Check if VITE_BRAND_API_URL is configured
 * 2. If configured, fetch BrandConfig from API on mount
 * 3. Fall back to embedded BRAND_CONFIG if fetch fails or no API configured
 * 4. Provide a refetch() function to manually update config
 */
export function useBrandConfig(options: UseBrandConfigOptions = {}): UseBrandConfigResult {
  const { brandId, apiEndpoint, autoFetch = true } = options;

  const [config, setConfig] = useState<BrandConfig>(BRAND_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFromApi, setIsFromApi] = useState(false);

  const refetch = async () => {
    // Only attempt API fetch if backend is configured
    if (!isBrandApiConfigured() && !apiEndpoint) {
      setError('Brand API not configured');
      setIsFromApi(false);
      setConfig(BRAND_CONFIG);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiConfig = await fetchBrandConfig(brandId, apiEndpoint);

      if (apiConfig) {
        setConfig(apiConfig);
        setIsFromApi(true);
      } else {
        // API fetch failed or returned null - fall back to embedded config
        setConfig(BRAND_CONFIG);
        setIsFromApi(false);
        setError('Failed to load from API, using embedded configuration');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setConfig(BRAND_CONFIG);
      setIsFromApi(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
  }, [brandId, apiEndpoint, autoFetch]);

  return {
    config,
    isLoading,
    error,
    isFromApi,
    refetch,
  };
}
